import React, { useRef, useEffect, useState } from "react"
import ScrollContainer from "react-indiana-drag-scroll"
import Masonry from "react-masonry-css"
import { useScrollPosition } from "@n8tb1t/use-scroll-position"

import * as s from "./Gallery.module.scss"

import { Image } from "@components/common/image/Image"
import { Video } from "@components/common/video/Video"
import Arrow from "@assets/icons/arrow.inline.svg"

export const Gallery = ({ data }) => {
  const { media, type, scrollText } = data
  const _scroller = useRef(null)
  const [isScrolling, setIsScrolling] = useState(null)
  const requestRef = useRef(null)

  const handleKeyDown = event => {
    if (!_scroller.current) return

    if (event.keyCode === 37) {
      setIsScrolling(-1)
    } else if (event.keyCode === 39) {
      setIsScrolling(1)
    }
  }

  if (typeof window !== `undefined`) {
    useScrollPosition(({ prevPos, currPos }) => {
      if (!_scroller.current) return
      const dir = currPos.y > prevPos.y ? -1 : 1
      const yPos =
        _scroller.current.container.current.getBoundingClientRect().top
      const dif = window.innerHeight - yPos

      if (dif > 1) {
        _scroller.current.container.current.scrollLeft =
          _scroller.current.container.current.scrollLeft + 3 * dir
      }
    })
  }

  const step = () => {
    _scroller.current.container.current.scrollLeft =
      _scroller.current.container.current.scrollLeft + isScrolling * 10

    if (isScrolling !== null) requestRef.current = requestAnimationFrame(step)
  }

  useEffect(() => {
    if (isScrolling !== null) requestRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(requestRef.current)
  }, [isScrolling])

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown)

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  if (!media || !type) {
    return <></>
  }

  const renderMediaItem = (mediaItem, i) => {
    if (mediaItem.type === "image") {
      return <Image key={i} data={mediaItem.image[0]} visibleByDefault={true} />
    } else if (mediaItem.type === "video") {
      return (
        <div key={i} className={s.VideoContainer}>
          <Video url={mediaItem.video[0].videoUrl} cover={true} />
        </div>
      )
    }
  }

  if (type === "flat") {
    return (
      <div
        className={`${s.Gallery} ${media.length == 1 && s.Gallery___single}`}
      >
        {data.media.map((mediaItem, i) => {
          return renderMediaItem(mediaItem, i)
        })}
      </div>
    )
  } else if (type === "scroll") {
    return (
      <div className={s.GalleryScroll}>
        <div className={s.GalleryScroll__header}>
          {scrollText && (
            <div>
              <span>{scrollText}</span>
            </div>
          )}

          <div
            className={s.GalleryScroll__header___left}
            onMouseOver={() => setIsScrolling(-1)}
            onMouseOut={() => setIsScrolling(null)}
          >
            <Arrow />
          </div>
          <div
            className={s.GalleryScroll__header___right}
            onMouseOver={() => setIsScrolling(1)}
            onMouseOut={() => setIsScrolling(null)}
          >
            <Arrow />
          </div>
        </div>

        <div className={s.GalleryScroll__inner}>
          <ScrollContainer
            className={s.GalleryScroll__container}
            hideScrollbars={true}
            vertical={false}
            ref={_scroller}
          >
            {data.media.map((mediaItem, i) => {
              return renderMediaItem(mediaItem, i)
            })}
          </ScrollContainer>
        </div>
      </div>
    )
  } else if (type === "masonry") {
    return (
      <div className={s.GalleryMasonry}>
        <Masonry
          breakpointCols={{ default: 2, 1023: 1 }}
          className={s.GalleryMasonry__grid}
        >
          {data.media.map((mediaItem, i) => {
            return renderMediaItem(mediaItem, i)
          })}
        </Masonry>
      </div>
    )
  }
}
